// NO RUNTIME DEPENDENCIES!
//
// This module is the single source of truth for PostHog product-analytics event
// names and person/group property shapes. It is imported by BOTH the browser
// (apps/oak via posthog-js) and Node services (encamp-posthog via posthog-node),
// so it must stay dependency-free and side-effect-free.
//
// Scout has its own historical catalog in `encamp-posthog/src/scoutEvents.ts`
// (event names prefixed `scout_`). Those predate this file and stay where they
// are; everything non-Scout lives here so client and server never drift on a
// raw string.
//
// Naming convention: snake_case, verb-led. Edits/creates use `edited_*` /
// `created_*` / `completed_*`, access of a surface uses `accessed_*`. Keep
// names stable once shipped — dashboards key off them.
//
// Page/surface views are deliberately NOT bespoke events: they're derivable
// from the URL, so we emit only `$pageview` and define named per-page metrics
// as PostHog Actions (URL-matched) in the PostHog UI. Add an event here only
// when it carries information the URL doesn't.

/**
 * Canonical non-Scout product event names. Use these constants instead of raw
 * strings so a typo is a compile error and "all product events" greps here.
 */
export const PRODUCT_POSTHOG_EVENTS = {
  // --- Auth / session (client-owned) ---
  LOGGED_IN: "logged_in",

  // --- Edits / mutations (server-owned unless noted) ---
  EDITED_CHEMICAL_INVENTORY: "edited_chemical_inventory",
  EDITED_CHEMICAL_CATALOG_ENTRY: "edited_chemical_catalog_entry",
  EDITED_FACILITY_PROFILE: "edited_facility_profile",
  CREATED_TASK: "created_task",
  EDITED_TASK: "edited_task",
  COMPLETED_TASK: "completed_task",
  CREATED_DOCUMENT: "created_document",
  EDITED_DOCUMENT: "edited_document",
  DOCUMENT_AI_REVIEWED: "document_ai_reviewed",
  USER_PROVISIONED: "user_provisioned",

  // --- Upper-right / help menu surfaces (client-owned) ---
  ACCESSED_LEGAL_MENU: "accessed_legal_menu",
  ACCESSED_FRONT_CHAT: "accessed_front_chat",
  ACCESSED_HELP: "accessed_help",
} as const;

export type ProductPosthogEvent =
  (typeof PRODUCT_POSTHOG_EVENTS)[keyof typeof PRODUCT_POSTHOG_EVENTS];

/**
 * Canonical Scout PostHog event names. Lives here (not in encamp-posthog) so the
 * browser can emit pure client-side Scout UI events — e.g. the Pro upsell
 * "learn more" click — without a server round-trip. `encamp-posthog` re-exports
 * this for server call sites (turns, ratings), keeping one source of truth.
 */
export const SCOUT_POSTHOG_EVENTS = {
  TURN_COMPLETED: "scout_turn_completed",
  TOOL_INVOKED: "scout_tool_invoked",
  CONVERSATION_ENDED: "scout_conversation_ended",
  MESSAGE_RATED: "scout_message_rated",
  PRO_UPSELL_LEARN_MORE_CLICKED: "scout_pro_upsell_learn_more_clicked",
  LAUNCH_ANNOUNCEMENT_SHOWN: "scout_launch_announcement_shown",
  LAUNCH_ANNOUNCEMENT_DISMISSED: "scout_launch_announcement_dismissed",
  LAUNCH_ANNOUNCEMENT_LEARN_MORE_CLICKED: "scout_launch_announcement_learn_more_clicked",
  LAUNCH_ANNOUNCEMENT_TOUR_STARTED: "scout_launch_announcement_tour_started",
  LAUNCH_ANNOUNCEMENT_TOUR_STEP_VIEWED: "scout_launch_announcement_tour_step_viewed",
  LAUNCH_ANNOUNCEMENT_TOUR_COMPLETED: "scout_launch_announcement_tour_completed",
  LAUNCH_ANNOUNCEMENT_TOUR_DISMISSED: "scout_launch_announcement_tour_dismissed",
} as const;

export type ScoutPosthogEvent = (typeof SCOUT_POSTHOG_EVENTS)[keyof typeof SCOUT_POSTHOG_EVENTS];

/**
 * Where a Scout Pro upsell "learn more" click originated. Emitted as the
 * `placement` property on `scout_pro_upsell_learn_more_clicked`.
 */
export const SCOUT_UPSELL_PLACEMENTS = {
  ASSISTANT_MESSAGE_CARD: "assistant_message_card",
  FILE_UPLOAD_BANNER: "file_upload_banner",
} as const;

export type ScoutUpsellPlacement =
  (typeof SCOUT_UPSELL_PLACEMENTS)[keyof typeof SCOUT_UPSELL_PLACEMENTS];

/**
 * `accessed_legal_menu` distinguishes which upper-right legal link was opened.
 */
export const LEGAL_MENU_TARGETS = {
  SECURITY: "security",
  PRIVACY: "privacy",
  TERMS_OF_SERVICE: "terms_of_service",
} as const;

export type LegalMenuTarget = (typeof LEGAL_MENU_TARGETS)[keyof typeof LEGAL_MENU_TARGETS];

/**
 * Outcome of a Document AI metadata review — the numerator/denominator for the
 * "extraction acceptance rate" CSM metric.
 */
export const DOCUMENT_AI_REVIEW_OUTCOMES = {
  CONFIRMED: "confirmed",
  CORRECTED: "corrected",
} as const;

export type DocumentAiReviewOutcome =
  (typeof DOCUMENT_AI_REVIEW_OUTCOMES)[keyof typeof DOCUMENT_AI_REVIEW_OUTCOMES];

/**
 * PostHog person properties set via `identify` / `$set`. Snake_case to match
 * PostHog conventions and keep dashboards readable. All optional so callers can
 * set whatever they have without null-padding.
 */
export interface PostHogPersonProperties {
  email?: string;
  name?: string;
  /** Role label for the currently-selected tenant (e.g. "Org Admin"). */
  role?: string;
  /** True for Encamp staff users (used to exclude internal traffic). */
  is_staff?: boolean;
  organization?: string;
  tenant_id?: string;
  /** User account status (e.g. ACTIVE, PENDING). */
  status?: string;
  /** ISO timestamp the user account was created. */
  created_at?: string;
  /** ISO timestamp of the user's first recorded login. */
  first_login_at?: string;
  /** ISO timestamp of the user's most recent login. */
  last_login_at?: string;
  /** Duration in ms of the user's most recent completed browser session. */
  last_session_length_ms?: number;
}

/**
 * PostHog tenant-group properties. `groupIdentify` on the `tenant` group so CSM
 * dashboards can break down by organization health without joining app data.
 */
export interface PostHogTenantGroupProperties {
  name?: string;
  /** Count of provisioned (non-deleted) users — denominator for active/provisioned. */
  provisioned_user_count?: number;
  /** Modules the tenant is entitled to (e.g. ["chemicals", "waste"]). */
  enabled_modules?: string[];
}
